'use client'

import * as React from 'react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { toast } from '@/lib/toast'

import { AuthError } from '@supabase/supabase-js'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertCircle, ArrowRight, Check, ChevronDown, WifiOff } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@luckagents/ui/primitives/button'
import { Input } from '@luckagents/ui/primitives/text-field'
import { Label } from '@luckagents/ui/primitives/label'
import { PasswordInput } from '@/components/ui/password-input'
import {
  DashboardExchangeError,
  exchangeDashboardSession,
  isRateLimitedExchangeError,
  register as registerAccount,
} from '@/lib/auth'
import {
  redirectToDashboard,
  redirectToStripeIfNeeded,
  safeSupabaseSignOut,
} from '@/lib/auth-helpers'
import { getSupabaseClient } from '@/lib/supabase'
import { withoutTrailingSlash } from '@/lib/utils'
import { GoogleIcon } from '@/components/icons/google'
import { buildTenantIdentity } from '@/lib/tenant'
import { getStripeClient } from '@/lib/stripe'
import { extractPlanHintFromLocation } from '@/lib/plan'
import {
  extractBillingPeriodFromLocation,
  normalizeBillingPeriod,
  type BillingPeriod,
} from '@/lib/billing'
import { logger } from '@/lib/logger'
import { formatRateLimitMessage, useRateLimitCooldown } from '@/hooks/use-rate-limit'
import { consumeRateLimitNotice } from '@/lib/rate-limit-notice'
import { trackMetaPixel } from '@/lib/meta-pixel'

/* ------------------------------------------------------------------ */
/*  Shared input class                                                 */
/* ------------------------------------------------------------------ */
const AUTH_INPUT_CLASS =
  'h-12 rounded-xl bg-zinc-50 px-4 text-[15px] transition-all duration-200 border border-zinc-200 focus:bg-white focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/5 dark:bg-zinc-900/50 dark:border-zinc-800 dark:focus:bg-zinc-900 dark:focus:border-zinc-600 dark:focus:ring-white/5 placeholder:text-zinc-400 dark:placeholder:text-zinc-500'

const AUTH_INPUT_ERROR = 'border-red-400 focus:border-red-500 focus:ring-red-500/10'
const AUTH_INPUT_VALID = 'border-emerald-300 focus:border-emerald-400 focus:ring-emerald-500/10'

const AUTH_EASE: [number, number, number, number] = [0.25, 0.4, 0.55, 1]

/* ------------------------------------------------------------------ */
/*  Stagger animation helpers                                          */
/* ------------------------------------------------------------------ */
const stagger = {
  container: {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.04 },
    },
  },
  item: {
    hidden: { opacity: 0, y: 8 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: AUTH_EASE },
    },
  },
}

/* ------------------------------------------------------------------ */
/*  Schema                                                             */
/* ------------------------------------------------------------------ */
const registerSchema = z.object({
  email: z.string().email('Ingresa un correo válido'),
  businessName: z.string().trim().max(80, 'Máximo 80 caracteres').optional(),
  password: z
    .string()
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .regex(/[A-Z]/, 'Debe contener al menos una mayúscula')
    .regex(/[0-9]/, 'Debe contener al menos un número'),
})

type RegisterFormData = z.infer<typeof registerSchema>

type RegisterFormCardProps = {
  planHint?: string | null
  billingPeriodHint?: BillingPeriod | string | null
  redirectToCheckout?: boolean
  oauthRedirectPath?: string
}

export function RegisterFormCard({
  planHint: planHintProp,
  billingPeriodHint: billingPeriodHintProp,
  redirectToCheckout = false,
  oauthRedirectPath,
}: RegisterFormCardProps = {}) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [ssoLoading, setSsoLoading] = React.useState(false)
  const [formError, setFormError] = React.useState('')
  const [submitSuccess, setSubmitSuccess] = React.useState(false)
  const [showBusinessName, setShowBusinessName] = React.useState(false)
  const { cooldownSeconds, isCoolingDown, startCooldown } = useRateLimitCooldown()
  const supabase = React.useMemo(() => getSupabaseClient(), [])

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, dirtyFields, touchedFields },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      businessName: '',
      password: '',
    },
  })

  const watchEmail = useWatch({ control, name: 'email' }) ?? ''
  const watchPassword = useWatch({ control, name: 'password' }) ?? ''

  /* Inline validation states */
  const emailValid =
    dirtyFields.email && touchedFields.email && !errors.email && watchEmail.includes('@')
  const passwordValid = dirtyFields.password && !errors.password && watchPassword.length >= 8

  React.useEffect(() => {
    const notice = consumeRateLimitNotice()
    if (!notice) return
    const remaining = notice.until - Date.now()
    if (remaining > 0) {
      startCooldown(remaining)
    }
  }, [startCooldown])

  const forceSupabaseLogout = React.useCallback(async () => {
    await safeSupabaseSignOut(supabase)
  }, [supabase])

  /* Determine error type for refined messaging */
  const isNetworkError = formError.includes('conexión') || formError.includes('conectar')

  const onSubmit = async (data: RegisterFormData) => {
    setFormError('')
    if (isCoolingDown) return
    const planHint = planHintProp ?? searchParams.get('plan')
    const billingPeriod = normalizeBillingPeriod(
      billingPeriodHintProp ?? searchParams.get('period')
    )

    try {
      const { tenantId, tenantName } = buildTenantIdentity({
        organizationName: data.businessName?.trim() || null,
        email: data.email,
        userName: data.email.split('@')[0] || 'Usuario',
      })
      const result = await registerAccount({
        email: data.email,
        password: data.password,
        organizationName: tenantName,
        tenantId,
        name: undefined,
      })

      if (result.session) {
        let exchangeResult
        try {
          exchangeResult = await exchangeDashboardSession(result.session, {
            plan: planHint,
            billingPeriod,
          })
        } catch (error) {
          if (isRateLimitedExchangeError(error)) {
            startCooldown(error.retryAfterMs)
            return
          }
          await forceSupabaseLogout()
          if (error instanceof DashboardExchangeError) {
            logger.error('[Auth] No fue posible sincronizar la nueva sesión con el dashboard', {
              code: error.code,
              error,
            })
          } else {
            logger.error('[Auth] Error inesperado al sincronizar la sesión con el dashboard', {
              error,
            })
          }
          setFormError('No pudimos sincronizar tu sesión con el dashboard. Intenta nuevamente.')
          return
        }

        if (!exchangeResult) {
          await forceSupabaseLogout()
          setFormError('No pudimos sincronizar tu sesión con el dashboard. Intenta nuevamente.')
          return
        }

        trackMetaPixel('CompleteRegistration', {
          method: 'password',
          plan: planHint ?? undefined,
          billingPeriod: billingPeriod ?? undefined,
        })

        const stripeInfo = exchangeResult.organization?.integrations?.stripe
        if (
          !redirectToCheckout &&
          stripeInfo?.setupIntentClientSecret &&
          !stripeInfo.defaultPaymentMethodId
        ) {
          const stripe = await getStripeClient()
          if (!stripe) {
            await forceSupabaseLogout()
            setFormError('Falta configurar la llave pública de Stripe. Contacta al administrador.')
            return
          }

          const setupResult = await stripe.confirmCardSetup(stripeInfo.setupIntentClientSecret)
          if (setupResult.error) {
            await forceSupabaseLogout()
            setFormError(setupResult.error.message ?? 'No pudimos confirmar el método de pago.')
            return
          }

          trackMetaPixel('AddPaymentInfo', {
            method: 'card_setup',
            plan: planHint ?? undefined,
            billingPeriod: billingPeriod ?? undefined,
          })
        }

        /* Success flash before redirect */
        setSubmitSuccess(true)

        if (redirectToStripeIfNeeded(exchangeResult, { enabled: redirectToCheckout })) {
          toast.success('Cuenta creada', {
            description: 'Redirigiendo a Stripe para completar el pago.',
          })
          return
        }

        toast.success('Cuenta creada', {
          description: 'Redirigiendo para continuar la configuración.',
        })

        redirectToDashboard((path) => router.push(path), searchParams.get('next'))
        return
      }

      toast.success('Verifica tu bandeja', {
        description: 'Confirma tu correo desde el enlace que te enviamos para activar la cuenta.',
      })
      trackMetaPixel('CompleteRegistration', {
        method: 'email',
        plan: planHint ?? undefined,
        billingPeriod: billingPeriod ?? undefined,
      })
    } catch (error) {
      if (error instanceof AuthError) {
        const message = error.message?.toLowerCase() ?? ''
        if (error.status === 429 || message.includes('rate')) {
          startCooldown()
        } else if (message.includes('network')) {
          setFormError(
            'No pudimos conectar con el servidor. Revisa tu conexión e inténtalo nuevamente.'
          )
        } else {
          setFormError(
            'No pudimos crear la cuenta con esos datos. Revisa la información e intenta de nuevo.'
          )
        }
      } else {
        setFormError('No pudimos crear la cuenta. Inténtalo de nuevo.')
      }
    }
  }

  async function handleSso() {
    setFormError('')
    if (isCoolingDown) return
    setSsoLoading(true)
    toast.info('Redirigiendo a Google', {
      description: 'Completa el acceso con tu cuenta corporativa.',
    })
    try {
      const portalBase =
        withoutTrailingSlash(process.env.NEXT_PUBLIC_PORTAL_URL ?? window.location.origin) ??
        window.location.origin
      const planHint = planHintProp ?? extractPlanHintFromLocation()
      const billingPeriod = normalizeBillingPeriod(
        billingPeriodHintProp ?? extractBillingPeriodFromLocation()
      )
      const params = new URLSearchParams()
      if (planHint) {
        params.set('plan', planHint)
      }
      if (billingPeriod) {
        params.set('period', billingPeriod)
      }
      const redirectPath =
        oauthRedirectPath ?? (params.toString() ? `/register?${params.toString()}` : '/register')

      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${portalBase}${redirectPath}`,
        },
      })
    } catch (error) {
      logger.error('[Auth] No pudimos continuar con Google', { error })
      setFormError('No pudimos continuar con Google. Intenta nuevamente.')
      setSsoLoading(false)
    }
  }

  const isDisabled = isSubmitting || ssoLoading || isCoolingDown

  return (
    <motion.div className="space-y-6" variants={stagger.container} initial="hidden" animate="show">
      {/* ---------- Google SSO (primary action) ---------- */}
      <motion.div variants={stagger.item}>
        <button
          type="button"
          onClick={() => void handleSso()}
          disabled={isDisabled}
          className="group relative flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 text-[15px] font-medium text-zinc-800 shadow-sm transition-all duration-200 hover:translate-y-[-1px] hover:border-zinc-300 hover:bg-zinc-50 hover:shadow-md active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-zinc-600 dark:hover:bg-zinc-800"
        >
          <GoogleIcon className="h-[18px] w-[18px] shrink-0" />
          <span>Registrarse con Google</span>
        </button>
        <p className="mt-1.5 text-center text-[11px] text-zinc-400 dark:text-zinc-500">
          La forma más rápida de empezar
        </p>
      </motion.div>

      {/* ---------- Divider ---------- */}
      <motion.div variants={stagger.item} className="relative py-1">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.3, duration: 0.5, ease: AUTH_EASE }}
            className="w-full origin-center border-t border-zinc-200/60 dark:border-zinc-800/60"
          />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-3 text-[11px] font-medium uppercase tracking-wide text-zinc-400 dark:bg-zinc-950 dark:text-zinc-500">
            o con email
          </span>
        </div>
      </motion.div>

      {/* ---------- Form ---------- */}
      <form
        className="space-y-4"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        aria-label="Crear cuenta"
      >
        {/* Email */}
        <motion.div variants={stagger.item} className="space-y-1">
          <Label htmlFor="email" className="sr-only">
            Correo electrónico
          </Label>
          <div className="relative">
            <Input
              id="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoFocus
              spellCheck={false}
              className={`${AUTH_INPUT_CLASS} ${errors.email ? AUTH_INPUT_ERROR : emailValid ? AUTH_INPUT_VALID : ''}`}
              placeholder="nombre@empresa.com"
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? 'register-email-error' : undefined}
              {...register('email')}
            />
            {emailValid && (
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500"
                aria-hidden="true"
              >
                <Check className="h-4 w-4" />
              </span>
            )}
          </div>
          <AnimatePresence mode="wait">
            {errors.email && (
              <motion.p
                id="register-email-error"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="text-xs font-medium text-red-500"
                role="status"
                aria-live="polite"
              >
                {errors.email.message}
              </motion.p>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Business name — collapsible optional field */}
        <motion.div variants={stagger.item}>
          {!showBusinessName ? (
            <button
              type="button"
              onClick={() => setShowBusinessName(true)}
              className="flex items-center gap-1.5 text-[13px] text-zinc-400 transition-colors hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300"
            >
              <ChevronDown className="h-3.5 w-3.5" />
              Agregar nombre de negocio
              <span className="text-[11px] text-zinc-400 dark:text-zinc-500">(opcional)</span>
            </button>
          ) : (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-1"
            >
              <Label htmlFor="businessName" className="sr-only">
                Nombre de tu negocio
              </Label>
              <Input
                id="businessName"
                type="text"
                autoComplete="organization"
                autoFocus
                className={`${AUTH_INPUT_CLASS} ${errors.businessName ? AUTH_INPUT_ERROR : ''}`}
                placeholder="Nombre de tu negocio"
                aria-invalid={Boolean(errors.businessName)}
                aria-describedby={
                  errors.businessName ? 'register-business-error' : 'register-business-hint'
                }
                {...register('businessName')}
              />
              <p
                id="register-business-hint"
                className="text-[11px] text-zinc-400 dark:text-zinc-500"
              >
                Puedes cambiarlo después.
              </p>
              <AnimatePresence mode="wait">
                {errors.businessName && (
                  <motion.p
                    id="register-business-error"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-xs font-medium text-red-500"
                    role="status"
                    aria-live="polite"
                  >
                    {errors.businessName.message}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </motion.div>

        {/* Password */}
        <motion.div variants={stagger.item} className="space-y-1">
          <Label htmlFor="password" className="sr-only">
            Contraseña
          </Label>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            strengthMeter
            className={`${AUTH_INPUT_CLASS} ${errors.password ? AUTH_INPUT_ERROR : passwordValid ? AUTH_INPUT_VALID : ''}`}
            placeholder="Crea una contraseña segura"
            aria-invalid={Boolean(errors.password)}
            aria-describedby={errors.password ? 'register-password-error' : undefined}
            {...register('password')}
          />
          <AnimatePresence mode="wait">
            {errors.password && (
              <motion.p
                id="register-password-error"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="text-xs font-medium text-red-500"
                role="status"
                aria-live="polite"
              >
                {errors.password.message}
              </motion.p>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Error alert — refined */}
        <AnimatePresence mode="wait">
          {(formError || isCoolingDown) && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className={`flex items-start gap-3 rounded-xl border p-3.5 text-[13px] leading-relaxed ${
                isNetworkError
                  ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-400'
                  : 'border-red-200 bg-red-50 text-red-600 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-400'
              }`}
              role="alert"
              aria-live="polite"
            >
              {isNetworkError ? (
                <WifiOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              ) : (
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              )}
              <span>{isCoolingDown ? formatRateLimitMessage(cooldownSeconds) : formError}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA Button */}
        <motion.div variants={stagger.item} className="pt-1">
          <Button
            type="submit"
            className={`group h-12 w-full rounded-xl text-[15px] font-semibold transition-all duration-200 active:scale-[0.98] ${
              submitSuccess
                ? 'bg-emerald-600 text-white shadow-sm hover:bg-emerald-600 dark:bg-emerald-500 dark:text-white'
                : 'bg-zinc-900 text-white shadow-md shadow-zinc-900/20 hover:bg-zinc-800 hover:shadow-lg hover:shadow-zinc-900/25 dark:bg-white dark:text-zinc-900 dark:shadow-white/10 dark:hover:bg-zinc-100 dark:hover:shadow-white/15'
            }`}
            disabled={isDisabled || submitSuccess}
            aria-busy={isSubmitting}
          >
            {submitSuccess ? (
              <span className="flex items-center gap-2">
                <Check className="h-5 w-5" aria-hidden="true" />
                Listo
              </span>
            ) : isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg
                  className="h-4 w-4 motion-safe:animate-spin"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Creando cuenta...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Empezar gratis
                <ArrowRight
                  className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </span>
            )}
          </Button>
        </motion.div>

        {/* Terms */}
        <motion.p
          variants={stagger.item}
          className="text-center text-[11px] leading-relaxed text-zinc-400 dark:text-zinc-500"
        >
          Al crear tu cuenta aceptas los{' '}
          <Link
            href="/terms"
            className="underline underline-offset-2 transition-colors hover:text-zinc-600 dark:hover:text-zinc-300"
          >
            Términos
          </Link>{' '}
          y la{' '}
          <Link
            href="/privacy"
            className="underline underline-offset-2 transition-colors hover:text-zinc-600 dark:hover:text-zinc-300"
          >
            Política de Privacidad
          </Link>
          .
        </motion.p>

        {/* Switch to login */}
        <motion.div variants={stagger.item} className="pt-2 text-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            ¿Ya tienes cuenta?{' '}
            <Link
              href="/access"
              className="font-medium text-zinc-900 underline-offset-2 transition-colors hover:underline dark:text-white"
            >
              Inicia sesión
            </Link>
          </p>
        </motion.div>
      </form>
    </motion.div>
  )
}
